import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import MindMapEditor from './components/mind-map-editor';
import { useMindMaps, mindMapKeys } from '@/hooks/useMindMaps';
import { useProjectMeta } from '@/hooks/meta/useProjectMeta';
import { createMindMap } from '@/services/supabase/mind-maps';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';

export default function MindMapPage() {
  const { id, projectId } = useParams<{ id: string; projectId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isViewMode = window.location.pathname.includes('/view');
  const [loading, setLoading] = useState(true);

  // Get the current story ID
  const { currentStoryId } = useProjectMeta(projectId || null);
  const storyId = currentStoryId?.toString();

  // Fetch mind maps for the current story
  const { data: mindMaps = [], isLoading: isFetchingMindMaps } = useMindMaps(storyId, { enabled: !!storyId });

  // Handle mind map creation or navigation
  useEffect(() => {
    // Wait until we have the story ID and project data is loaded
    if (!storyId || !projectId || isFetchingMindMaps) return;

    const handleMindMapNavigation = async () => {
      try {
        // If we have an ID, just show that mind map
        if (id) {
          setLoading(false);
          return;
        }

        // If we have mind maps, navigate to the first one
        if (mindMaps.length > 0) {
          navigate(`/projects/${projectId}/mind-maps/${mindMaps[0].id}`, { replace: true });
          return;
        }

        // Otherwise create a new mind map
        const newMindMap = await createMindMap({
          title: 'Mind Map',
          story_id: storyId,
          content: {}
        });

        // Invalidate the mind maps query to refresh the list
        queryClient.invalidateQueries({ queryKey: mindMapKeys.list(storyId) });
        toast.success('Mind map created successfully');

        // Navigate to the new mind map
        navigate(`/projects/${projectId}/mind-maps/${newMindMap.id}`, { replace: true });
      } catch (error) {
        console.error('Error handling mind map:', error);
        toast.error('Failed to load or create mind map');
        setLoading(false);
      }
    };

    handleMindMapNavigation();
  }, [id, storyId, projectId, mindMaps, navigate, isFetchingMindMaps, queryClient]);

  if (loading && !id) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="h-full overflow-hidden">
      <MindMapEditor readOnly={isViewMode} />
    </div>
  );
}